import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Plus, Feather, Wand2, ShieldCheck, Users, ListTree,
    Loader2, Trash2, ChevronRight, Send, Save, Download, History,
    Focus, Check, X, FileText, Target, Flame, Image as ImageIcon, Sparkles,
    Circle, CircleDot, CircleCheck, Share2, Link as LinkIcon, MessageCircle, Copy, Upload,
    EyeOff, Eye, Quote, Menu, WifiOff, Cloud,
} from "lucide-react";
import { toast } from "sonner";
import HelpMenu from "@/components/HelpMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import RichEditor from "@/components/RichEditor";
import { sanitizeHtml } from "@/lib/sanitize";
import { useGate } from "@/lib/gate";
import { useAuth } from "@/lib/auth";
import { MODELS, effectiveModel, isModelAllowed } from "@/lib/models";
import { useTranslation } from "@/lib/i18n";
import LockedButton from "@/components/LockedButton";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    getNovel, listChapters, createChapter, updateChapter, deleteChapter,
    listCharacters, createCharacter, updateCharacter, deleteCharacter, CHARACTER_PALETTE,
    listPlot, createPlot, deletePlot,
    listVersions, restoreVersion, getVersion,
    novelStats, logWritingSession, exportNovelUrl, updateNovel,
    listScenes, createScene, updateScene, deleteScene,
    uploadCover, deleteCover, proofreadJSON,
    enableShare, disableShare, listNovelComments, deleteComment,
    reorderChapters, reorderScenes,
    absUrl,
    streamAI, API, updateSettings,
} from "@/lib/api";


// One place that turns an AI failure into what the writer sees. A 429 used
// to surface as "AI error: HTTP 429"; running out of AI words now opens the
// upgrade dialog with the server's own message, which says whether these
// were Seer's free words or this month's allowance.
function handleAIError({ status, reason, detail }, gate, nav) {
    if (status === 402) {
        toast.error("AI features require a paid plan");
        nav("/pricing");
    } else if (reason === "ai_words_exhausted") {
        gate.openPaywall("ai", { title: "You've used your AI words", body: detail });
    } else {
        toast.error(detail || "The AI assistant is unavailable right now. Please try again.");
    }
}

const EDIT_PRESETS = [
    { key: "ws.preset.sensory", instruction: "Rewrite for stronger sensory detail" },
    { key: "ws.preset.concise", instruction: "Make this more concise" },
    { key: "ws.preset.expand", instruction: "Expand this paragraph" },
    { key: "ws.preset.pastTense", instruction: "Change to past tense" },
    { key: "ws.preset.tension", instruction: "Increase tension and pacing" },
    { key: "ws.preset.dialogue", instruction: "Improve dialogue naturalness" },
];

function htmlToWordCount(html) {
    if (!html) return 0;
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const text = tmp.textContent || "";
    return text.trim().split(/\s+/).filter(Boolean).length;
}

// --- Offline draft persistence (survives weak signal / reload, auto-syncs on reconnect) ---
const DRAFT_PREFIX = "dd_draft_";
function saveLocalDraft(chapterId, data) {
    try { localStorage.setItem(DRAFT_PREFIX + chapterId, JSON.stringify({ ...data, ts: Date.now() })); } catch {}
}
function readLocalDraft(chapterId) {
    try { return JSON.parse(localStorage.getItem(DRAFT_PREFIX + chapterId)); } catch { return null; }
}
function clearLocalDraft(chapterId) {
    try { localStorage.removeItem(DRAFT_PREFIX + chapterId); } catch {}
}
function listDirtyDraftIds() {
    try { return Object.keys(localStorage).filter(k => k.startsWith(DRAFT_PREFIX)).map(k => k.slice(DRAFT_PREFIX.length)); } catch { return []; }
}


function extractProofreadTerms(text) {
    // Parse AI proofread output: look for "...quoted..." or `term` or - quote/term
    if (!text) return [];
    const terms = new Set();
    // single-quoted, double-quoted, backtick-quoted phrases
    const re = /["“”']([^"“”']{2,80})["“”']|`([^`]{2,80})`/g;
    let m;
    while ((m = re.exec(text)) !== null) {
        const t = (m[1] || m[2] || "").trim();
        if (t) terms.add(t);
    }
    return Array.from(terms).slice(0, 25);
}

export default function Workspace() {
    const { novelId } = useParams();
    const nav = useNavigate();
    const gate = useGate();
    const { t } = useTranslation();
    const [novel, setNovel] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [activeChapter, setActiveChapter] = useState(null);
    const [content, setContent] = useState("");
    const [title, setTitle] = useState("");
    const [saving, setSaving] = useState(false);
    const { user, setUser } = useAuth();
    const allowedModels = gate.me?.allowed_models;
    const allowedKey = (allowedModels || []).join(",");
    // Was hardcoded to "claude-sonnet-5", so the model chosen in Settings
    // was never used. Start from the saved preference, falling back to the
    // default when the user's plan does not include it.
    const [model, setModel] = useState(() => effectiveModel(user?.preferred_model, allowedModels));
    useEffect(() => {
        setModel(effectiveModel(user?.preferred_model, allowedModels));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.preferred_model, allowedKey]);

    // Choosing a model here saves it as the preference too, so Settings and
    // the Workspace always agree and the choice survives a reload.
    async function pickModel(next) {
        setModel(next);
        try {
            await updateSettings({ preferred_model: next });
            // In place, not a refetch: a refetch made a new user object and
            // briefly reset the plan gate on every model change.
            setUser((u) => (u ? { ...u, preferred_model: next } : u));
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't save your model choice");
        }
    }
    const [aiBusy, setAiBusy] = useState(false);
    const [aiOutput, setAiOutput] = useState("");
    const [aiInstruction, setAiInstruction] = useState("");
    const [tab, setTab] = useState("write");
    const [focusMode, setFocusMode] = useState(false);
    const [mobileChaptersOpen, setMobileChaptersOpen] = useState(false);
    const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
    const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
    const [syncState, setSyncState] = useState("saved"); // "saved" | "saving" | "offline" | "limit"
    const limitNoticeShown = useRef(false);
    const chaptersRef = useRef([]);
    const [stats, setStats] = useState({ total_words: 0, today_words: 0, streak: 0, daily_goal: 500 });
    const editorRef = useRef(null);
    const saveTimerRef = useRef(null);
    const startWordsRef = useRef(0);
    const initRef = useRef(false);
    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const [aiInsertCount, setAiInsertCount] = useState(0);
    const [proofUnresolvedCount, setProofUnresolvedCount] = useState(0);
    const [castCount, setCastCount] = useState(0);
    const [plotCount, setPlotCount] = useState(0);
    const tabCounts = { proofread: proofUnresolvedCount, characters: castCount, plot: plotCount };

    const reloadChapters = useCallback(async () => {
        const list = await listChapters(novelId);
        setChapters(list);
        return list;
    }, [novelId]);

    const refreshStats = useCallback(async () => {
        try { setStats(await novelStats(novelId)); } catch (e) { console.error("Stats refresh failed", e); }
    }, [novelId]);

    useEffect(() => { chaptersRef.current = chapters; }, [chapters]);

    // Save a chapter to the server. On failure (weak signal/offline) the local
    // draft stays in localStorage and auto-syncs once the connection returns.
    const flushChapter = useCallback(async (id, ttl, cont, opts = {}) => {
        setSaving(true);
        setSyncState("saving");
        try {
            const updated = await updateChapter(id, { title: ttl, content: cont });
            clearLocalDraft(id);
            setActiveChapter(prev => (prev && prev.id === id ? updated : prev));
            setChapters(cs => cs.map(c => c.id === id ? updated : c));
            if (opts.logSession !== false) {
                const allChapters = chaptersRef.current.map(c => c.id === id ? updated : c);
                const totalWords = allChapters.reduce((acc, c) => acc + htmlToWordCount(c.content), 0);
                try { await logWritingSession(novelId, totalWords); } catch {}
                refreshStats();
            }
            setOnline(true);
            setSyncState("saved");
            limitNoticeShown.current = false;
            return true;
        } catch (e) {
            if (e?.response?.status === 402) {
                // Over the free plan's words-per-novel limit. The server refused
                // the save; the draft stays on this device (not lost) and the
                // upgrade dialog explains why, once per editing session.
                setOnline(true);
                setSyncState("limit");
                if (!limitNoticeShown.current) {
                    limitNoticeShown.current = true;
                    gate.openPaywall("words");
                }
                return false;
            }
            setOnline(false);
            setSyncState("offline");
            return false;
        } finally {
            setSaving(false);
        }
    }, [novelId, refreshStats, gate]);

    // Flush any drafts left on this device the moment the network returns.
    useEffect(() => {
        function syncPending() {
            setOnline(true);
            const ids = listDirtyDraftIds();
            if (ids.length === 0) { setSyncState("saved"); return; }
            ids.forEach(id => {
                const d = readLocalDraft(id);
                if (d) flushChapter(id, d.title ?? "", d.content ?? "", { logSession: false });
            });
            toast.success(t("ws.sync.reconnected"));
        }
        function goOffline() { setOnline(false); setSyncState("offline"); }
        window.addEventListener("online", syncPending);
        window.addEventListener("offline", goOffline);
        return () => {
            window.removeEventListener("online", syncPending);
            window.removeEventListener("offline", goOffline);
        };
    }, [flushChapter, t]);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        (async () => {
            try {
                const n = await getNovel(novelId);
                setNovel(n);
                const list = await reloadChapters();
                let ch;
                if (list.length === 0) {
                    ch = await createChapter(novelId, { title: "Chapter 1", content: "" });
                    setChapters([ch]);
                } else {
                    ch = list[0];
                }
                setActiveChapter(ch);
                const draft = readLocalDraft(ch.id);
                const hasDraft = draft && (draft.content !== ch.content || draft.title !== ch.title);
                setTitle(hasDraft && draft.title != null ? draft.title : ch.title);
                setContent(hasDraft && draft.content != null ? draft.content : (ch.content || ""));
                startWordsRef.current = htmlToWordCount(ch.content || "");
                if (hasDraft) toast.message(t("ws.sync.restored"));
                refreshStats();
            } catch (e) { toast.error("Failed to load novel"); }
        })();
    }, [novelId, reloadChapters, refreshStats]);

    // Autosave (local-first: persist to device instantly, then sync to server)
    useEffect(() => {
        if (!activeChapter) return;
        if (title === activeChapter.title && content === activeChapter.content) return;
        // Persist to this device immediately so nothing is lost on a weak signal or reload.
        saveLocalDraft(activeChapter.id, { title, content });
        clearTimeout(saveTimerRef.current);
        const id = activeChapter.id;
        saveTimerRef.current = setTimeout(() => { flushChapter(id, title, content); }, 900);
        return () => clearTimeout(saveTimerRef.current);
    }, [title, content, activeChapter, flushChapter]);

    // poll AI-insert count after each AI op
    useEffect(() => {
        const id = setInterval(() => {
            const n = editorRef.current?.countAIInserts?.() ?? 0;
            setAiInsertCount(n);
        }, 600);
        return () => clearInterval(id);
    }, []);

    function selectChapter(ch) {
        setMobileChaptersOpen(false);
        if (ch.id === activeChapter?.id) return;
        setActiveChapter(ch);
        const draft = readLocalDraft(ch.id);
        setTitle(draft && draft.title != null ? draft.title : ch.title);
        setContent(draft && draft.content != null ? draft.content : (ch.content || ""));
        setAiOutput("");
        startWordsRef.current = htmlToWordCount(ch.content || "");
        editorRef.current?.clearProofreadIssues?.();
    }

    async function addChapter() {
        const ch = await createChapter(novelId, { title: `Chapter ${chapters.length + 1}`, content: "" });
        setChapters([...chapters, ch]);
        selectChapter(ch);
    }

    async function removeChapter(id, e) {
        e.stopPropagation();
        if (!confirm("Delete this chapter?")) return;
        await deleteChapter(id);
        const list = await reloadChapters();
        if (list.length === 0) {
            const ch = await createChapter(novelId, { title: "Chapter 1", content: "" });
            setChapters([ch]); selectChapter(ch);
        } else if (id === activeChapter?.id) {
            selectChapter(list[0]);
        }
    }

    async function runAI(mode, instruction, options = {}) {
        if (aiBusy) return;
        if (!gate.canAI) { gate.openPaywall("ai"); return; }
        setAiBusy(true);
        setAiOutput("");
        const selected = editorRef.current?.getSelectedText?.() || "";
        const payload = {
            novel_id: novelId,
            chapter_id: activeChapter?.id,
            model, mode, instruction,
            selected_text: options.selectedText ?? (mode === "edit" || mode === "proofread" ? selected : ""),
            context: options.context ?? (mode === "continue" ? (editorRef.current?.getPlainText?.()?.slice(-2000) || "") : ""),
        };
        await streamAI(
            payload,
            (d) => setAiOutput(prev => prev + d),
            () => setAiBusy(false),
            (err) => {
                handleAIError(err || {}, gate, nav);
                setAiBusy(false);
            },
        );
    }

    const wc = useMemo(() => htmlToWordCount(content), [content]);
    const goalPct = Math.min(100, Math.round((stats.today_words / Math.max(1, stats.daily_goal)) * 100));

    if (!novel) {
        return <div className="h-screen flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("ws.openingManuscript")}
        </div>;
    }

    return (
        <div className={`h-screen flex flex-col bg-background ${focusMode ? "focus-mode" : ""}`}>
            {/* Persistent exit-focus chip (only visible in focus mode) */}
            {focusMode && (
                <button
                    data-testid="exit-focus-chip"
                    onClick={() => setFocusMode(false)}
                    className="fixed top-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card/90 backdrop-blur border border-border shadow-sm text-xs hover:bg-card transition-colors"
                >
                    <Focus className="w-3 h-3" /> {t("ws.exitFocus")}
                </button>
            )}
            {/* Top bar */}
            <header className="focus-hide flex items-center gap-2 px-3 md:px-5 h-14 border-b border-border/60 bg-card/60 backdrop-blur-md">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <Button data-testid="mobile-chapters-toggle" variant="ghost" size="icon" className="md:hidden shrink-0" onClick={() => setMobileChaptersOpen(true)}>
                        <Menu className="w-5 h-5" />
                    </Button>
                    <Button data-testid="back-to-dashboard" variant="ghost" size="sm" className="shrink-0" onClick={() => nav("/")}>
                        <ArrowLeft className="w-4 h-4" /> <span className="hidden lg:inline">{t("ws.library")}</span>
                    </Button>
                    <div className="h-5 w-px bg-border hidden md:block" />
                    <Feather className="w-4 h-4 text-primary hidden md:block shrink-0" />
                    <EditableTitle
                        value={novel.title}
                        onSave={async (next) => {
                            const t = next.trim();
                            if (!t || t === novel.title) return;
                            try {
                                const updated = await updateNovel(novel.id, { title: t });
                                setNovel(updated);
                                toast.success(t("ws.titleUpdated"));
                            } catch { toast.error("Couldn't save title"); }
                        }}
                    />
                    {novel.genre && <span className="ui-label hidden md:inline whitespace-nowrap">· {novel.genre}</span>}
                </div>
                <div className="flex flex-nowrap items-center gap-1.5 md:gap-3 shrink-0 overflow-x-auto no-scrollbar max-w-[60%] md:max-w-none [&>*]:shrink-0">
                    <GoalIndicator stats={stats} pct={goalPct} />
                    <span className="ui-label hidden md:inline">{wc.toLocaleString()} {t("ws.words")}</span>
                    <span data-testid="save-indicator" className={`ui-label flex items-center gap-1.5 ${syncState === "offline" || syncState === "limit" ? "text-amber-600" : ""}`}>
                        {syncState === "limit"
                            ? <><Save className="w-3 h-3" /> <span className="hidden sm:inline">{t("ws.sync.limit")}</span></>
                            : syncState === "offline"
                            ? <><WifiOff className="w-3 h-3" /> <span className="hidden sm:inline">{t("ws.sync.offline")}</span></>
                            : saving
                                ? <><Loader2 className="w-3 h-3 animate-spin" /> <span className="hidden sm:inline">{t("ws.saving")}</span></>
                                : <><Save className="w-3 h-3" /> <span className="hidden sm:inline">{t("ws.saved")}</span></>}
                    </span>

                    <VersionsDialog chapterId={activeChapter?.id} onRestore={async () => {
                        const ch = await listChapters(novelId).then(list => list.find(c => c.id === activeChapter.id));
                        if (ch) { setActiveChapter(ch); setContent(ch.content); setTitle(ch.title); }
                    }} />

                    <ExportMenu novelId={novelId} />

                    <ShareButton novel={novel} onUpdated={setNovel} />

                    <CommentsButton novelId={novelId} chapters={chapters} />

                    <CoverButton novel={novel} onUpdated={(updated) => setNovel(updated)} />

                    <Button
                        data-testid="focus-mode-toggle"
                        variant={focusMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFocusMode(f => !f)}
                        title="Focus mode (Cmd/Ctrl+.)"
                    >
                        <Focus className="w-4 h-4" /> {focusMode ? t("ws.exitFocus") : t("ws.focus")}
                    </Button>

                    <Select value={model} onValueChange={pickModel}>
                        <SelectTrigger data-testid="model-selector" className="w-[132px] md:w-[170px] h-9 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {MODELS.map(m => {
                                const allowed = isModelAllowed(m.value, allowedModels);
                                return (
                                    <SelectItem key={m.value} value={m.value} disabled={!allowed} data-testid={`model-option-${m.value}`}>
                                        {m.label}{allowed ? "" : " · Magnus+"}
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>

                    <HelpMenu variant="light" align="end" />

                    <Button data-testid="mobile-tools-toggle" variant="outline" size="icon" className="md:hidden" onClick={() => setMobileToolsOpen(true)}>
                        <Sparkles className="w-4 h-4" />
                    </Button>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden">
                {(mobileChaptersOpen || mobileToolsOpen) && (
                    <div
                        data-testid="mobile-panel-backdrop"
                        className="fixed inset-0 top-14 z-30 bg-black/40 md:hidden"
                        onClick={() => { setMobileChaptersOpen(false); setMobileToolsOpen(false); }}
                    />
                )}
                {/* Left sidebar: chapters (drawer on mobile) */}
                <aside className={`focus-hide fixed top-14 bottom-0 left-0 z-40 w-[82%] max-w-[300px] bg-card border-r border-border/60 flex flex-col transition-transform duration-200 md:static md:top-auto md:bottom-auto md:z-auto md:w-[260px] md:max-w-none md:bg-card/30 md:translate-x-0 ${mobileChaptersOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}>
                    <div className="p-4 flex items-center justify-between">
                        <span className="ui-label">{t("ws.chapters")}</span>
                        <Button data-testid="add-chapter-button" variant="ghost" size="sm" onClick={addChapter}>
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                    <ScrollArea className="flex-1 px-2">
                        <DndContext
                            sensors={dndSensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(e) => {
                                const { active, over } = e;
                                if (!over || active.id === over.id) return;
                                const oldIdx = chapters.findIndex(c => c.id === active.id);
                                const newIdx = chapters.findIndex(c => c.id === over.id);
                                if (oldIdx < 0 || newIdx < 0) return;
                                const next = arrayMove(chapters, oldIdx, newIdx);
                                setChapters(next.map((c, i) => ({ ...c, order: i + 1 })));
                                reorderChapters(novelId, next.map(c => c.id)).catch(() => toast.error("Reorder failed"));
                            }}
                        >
                            <SortableContext items={chapters.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                <ul className="space-y-0.5 pb-4">
                                    {chapters.map((c) => (
                                        <SortableChapterItem
                                            key={c.id}
                                            chapter={c}
                                            isActive={activeChapter?.id === c.id}
                                            onSelect={() => selectChapter(c)}
                                            onDelete={(e) => removeChapter(c.id, e)}
                                        />
                                    ))}
                                </ul>
                            </SortableContext>
                        </DndContext>
                    </ScrollArea>
                </aside>

                {/* Center: editor */}
                <main className="flex-1 overflow-y-auto relative">
                    {/* Track-changes floating bar */}
                    {aiInsertCount > 0 && (
                        <div className="sticky top-3 z-10 flex justify-center pointer-events-none">
                            <div className="pointer-events-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/90 backdrop-blur-md border border-border shadow-sm">
                                <span className="text-xs text-muted-foreground">
                                    {aiInsertCount === 1 ? t("ws.tc.pendingOne", { n: aiInsertCount }) : t("ws.tc.pendingMany", { n: aiInsertCount })}
                                </span>
                                <Button data-testid="accept-all-button" size="sm" variant="ghost" className="h-7 text-xs"
                                    onClick={() => { editorRef.current?.acceptAllAIInserts(); toast.success(t("ws.tc.accepted")); }}>
                                    <Check className="w-3 h-3 text-primary" /> {t("ws.tc.acceptAll")}
                                </Button>
                                <Button data-testid="reject-all-button" size="sm" variant="ghost" className="h-7 text-xs"
                                    onClick={() => { editorRef.current?.rejectAllAIInserts(); toast.success(t("ws.tc.rejected")); }}>
                                    <X className="w-3 h-3 text-destructive" /> {t("ws.tc.rejectAll")}
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-16 py-8 md:py-12 lg:py-16 pb-28 md:pb-16 focus-canvas">
                        <input
                            data-testid="chapter-title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t("ws.chapterTitle")}
                            className="w-full bg-transparent font-heading text-4xl lg:text-5xl font-light tracking-tight outline-none placeholder:text-muted-foreground/40 mb-2"
                        />
                        <ChapterMetaBar
                            wc={wc}
                            chapter={activeChapter}
                            onGoalChange={async (goal) => {
                                if (!activeChapter) return;
                                const updated = await updateChapter(activeChapter.id, { word_goal: goal });
                                setActiveChapter(updated);
                                setChapters(cs => cs.map(c => c.id === updated.id ? updated : c));
                            }}
                        />
                        <SceneStrip
                            chapterId={activeChapter?.id}
                        />
                        <RichEditor
                            ref={editorRef}
                            content={content}
                            onChange={setContent}
                            placeholder={t("ws.beginChapter")}
                            focusMode={focusMode}
                        />
                    </div>
                </main>

                {/* Right panel (drawer on mobile) */}
                <aside className={`focus-hide fixed top-14 bottom-0 right-0 z-40 w-[88%] max-w-[380px] bg-card border-l border-border/60 flex flex-col transition-transform duration-200 md:static md:top-auto md:bottom-auto md:z-auto md:w-[380px] md:max-w-none md:bg-card/30 md:translate-x-0 ${mobileToolsOpen ? "translate-x-0 shadow-2xl" : "translate-x-full"}`}>
                    <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
                        <TabsList className="grid grid-cols-5 mx-3 mt-3 bg-transparent border-b rounded-none h-auto p-0">
                            {[
                                ["write", Wand2, t("ws.tab.write")],
                                ["edit", Feather, t("ws.tab.edit")],
                                ["proofread", ShieldCheck, t("ws.tab.proof")],
                                ["characters", Users, t("ws.tab.cast")],
                                ["plot", ListTree, t("ws.tab.plot")],
                            ].map(([v, Icon, label]) => (
                                <TabsTrigger
                                    key={v} value={v}
                                    data-testid={`tab-${v}`}
                                    className="flex flex-col items-center gap-1 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                                    <span className="relative">
                                        <Icon className="w-3.5 h-3.5" />
                                        {(tabCounts[v] || 0) > 0 && (
                                            <span data-testid={`tab-${v}-count`}
                                                className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-semibold flex items-center justify-center tabular-nums">
                                                {tabCounts[v]}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider">{label}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value="write" className="data-[state=inactive]:hidden flex-1 m-0 p-4 flex flex-col gap-3 overflow-hidden">
                            <p className="text-xs text-muted-foreground">{t("ws.write.descPre")}<span className="ai-insert">{t("ws.write.descTracked")}</span>.</p>
                            <Textarea data-testid="write-instruction"
                                value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)}
                                placeholder={t("ws.write.placeholder")}
                                rows={4} />
                            <div className="flex gap-2">
                                <LockedButton
                                    locked={!gate.canAI} reason="ai"
                                    data-testid="ai-write-button" className="flex-1" disabled={aiBusy}
                                    onClick={() => runAI("write", aiInstruction)}>
                                    {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {t("ws.write.draftScene")}
                                </LockedButton>
                                <LockedButton
                                    locked={!gate.canAI} reason="ai"
                                    data-testid="ai-continue-button" variant="outline" disabled={aiBusy}
                                    onClick={() => runAI("continue", aiInstruction || "Continue naturally.")}>
                                    {t("ws.write.continue")}
                                </LockedButton>
                            </div>
                            <AIPreview output={aiOutput} busy={aiBusy}
                                onInsert={() => { editorRef.current?.insertParagraphAfter(aiOutput); setAiOutput(""); }}
                                insertLabel={t("ws.write.insertChapter")} />
                        </TabsContent>

                        <TabsContent value="edit" className="data-[state=inactive]:hidden flex-1 m-0 p-4 flex flex-col gap-3 overflow-hidden">
                            <p className="text-xs text-muted-foreground">
                                {t("ws.edit.desc")}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {EDIT_PRESETS.map(p => (
                                    <LockedButton key={p.key} locked={!gate.canAI} reason="ai"
                                        variant="secondary" size="sm" className="text-xs h-7"
                                        data-testid={`edit-preset-${p.instruction.slice(0, 10).replace(/\s/g, '-')}`}
                                        disabled={aiBusy}
                                        onClick={() => runAI("edit", p.instruction)}>
                                        {t(p.key)}
                                    </LockedButton>
                                ))}
                            </div>
                            <Textarea data-testid="edit-instruction"
                                value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)}
                                placeholder={t("ws.edit.placeholder")} rows={3} />
                            <LockedButton locked={!gate.canAI} reason="ai"
                                data-testid="ai-edit-button" disabled={aiBusy}
                                onClick={() => runAI("edit", aiInstruction || "Rewrite for clarity and impact.")}>
                                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Feather className="w-4 h-4" />} {t("ws.edit.rewriteSelection")}
                            </LockedButton>
                            <AIPreview output={aiOutput} busy={aiBusy}
                                onInsert={() => { editorRef.current?.replaceSelectionWith(aiOutput); setAiOutput(""); }}
                                insertLabel={t("ws.edit.replaceSelection")} />
                        </TabsContent>

                        {/* Proof, Cast and Plot live in this column like Write and Edit.
                            They used to float over the page beside it, covering 49-65%
                            of the manuscript on a laptop, all of it on a tablet, and
                            opening off-screen on a phone, while this column showed only
                            a hint pointing at them. */}
                        <TabsContent value="proofread" data-testid="proof-tab-panel" className="data-[state=inactive]:hidden flex-1 m-0 p-4 flex flex-col gap-3 overflow-hidden min-h-0">
                            <ProofreadPanel
                                novelId={novelId}
                                chapterId={activeChapter?.id}
                                model={model}
                                editorRef={editorRef}
                                onIssueCountChange={setProofUnresolvedCount}
                            />
                        </TabsContent>

                        <TabsContent value="characters" data-testid="cast-tab-panel" className="data-[state=inactive]:hidden flex-1 m-0 p-4 flex flex-col overflow-hidden min-h-0">
                            <CharactersPanel
                                novelId={novelId}
                                onAI={runAI}
                                aiBusy={aiBusy}
                                aiOutput={aiOutput}
                                onCountChange={setCastCount}
                            />
                        </TabsContent>

                        <TabsContent value="plot" data-testid="plot-tab-panel" className="data-[state=inactive]:hidden flex-1 m-0 p-4 flex flex-col overflow-hidden min-h-0">
                            <PlotPanel
                                novelId={novelId}
                                onAI={runAI}
                                aiBusy={aiBusy}
                                aiOutput={aiOutput}
                                insertAtCursor={(t) => editorRef.current?.insertParagraphAfter(t)}
                                onCountChange={setPlotCount}
                            />
                        </TabsContent>
                    </Tabs>
                </aside>
            </div>

            {/* Mobile bottom tab bar — Chapters · Focus · Tools within thumb reach */}
            <nav
                data-testid="mobile-bottom-bar"
                className={`focus-hide fixed bottom-0 inset-x-0 z-40 h-16 border-t border-border/60 bg-card/95 backdrop-blur-md items-stretch ${(mobileChaptersOpen || mobileToolsOpen) ? "hidden" : "flex"} md:hidden`}
            >
                <button data-testid="bottombar-chapters" onClick={() => { setMobileToolsOpen(false); setMobileChaptersOpen(v => !v); }}
                    className={`flex-1 flex flex-col items-center justify-center gap-1 text-[11px] transition-colors ${mobileChaptersOpen ? "text-primary" : "text-muted-foreground"}`}>
                    <Menu className="w-5 h-5" /> {t("ws.bottombar.chapters")}
                </button>
                <button data-testid="bottombar-focus" onClick={() => { setMobileChaptersOpen(false); setMobileToolsOpen(false); setFocusMode(f => !f); }}
                    className={`flex-1 flex flex-col items-center justify-center gap-1 text-[11px] transition-colors ${focusMode ? "text-primary" : "text-muted-foreground"}`}>
                    <Focus className="w-5 h-5" /> {t("ws.bottombar.focus")}
                </button>
                <button data-testid="bottombar-tools" onClick={() => { setMobileChaptersOpen(false); setMobileToolsOpen(v => !v); }}
                    className={`flex-1 flex flex-col items-center justify-center gap-1 text-[11px] transition-colors ${mobileToolsOpen ? "text-primary" : "text-muted-foreground"}`}>
                    <Sparkles className="w-5 h-5" /> {t("ws.bottombar.tools")}
                </button>
            </nav>


        </div>
    );
}

function GoalIndicator({ stats, pct }) {
    return (
        <div className="hidden md:flex items-center gap-3" data-testid="goal-streak-indicator">
            <div className="flex items-center gap-1.5" title={`${stats.today_words}/${stats.daily_goal} words today`}>
                <Target className="w-3.5 h-3.5 text-primary" />
                <div className="w-20">
                    <Progress value={pct} className="h-1.5" />
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums">{stats.today_words}/{stats.daily_goal}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground" title="Day streak">
                <Flame className={`w-3.5 h-3.5 ${stats.streak > 0 ? "text-orange-500" : ""}`} />
                <span className="tabular-nums" data-testid="streak-count">{stats.streak}</span>
            </div>
        </div>
    );
}

function AIPreview({ output, busy, onInsert, insertLabel }) {
    if (!output && !busy) return null;
    return (
        <Card className="p-3 bg-background/60 flex-1 flex flex-col overflow-hidden min-h-[120px]">
            <ScrollArea className="flex-1 -mx-1 px-1">
                <pre data-testid="ai-output" className="whitespace-pre-wrap font-editor text-sm leading-relaxed text-foreground/90 pr-2">
                    {output}{busy && <span className="shimmer">▍</span>}
                </pre>
            </ScrollArea>
            {insertLabel && output && !busy && (
                <Button data-testid="ai-insert-button" size="sm" className="mt-2" onClick={onInsert}>
                    <Send className="w-3 h-3" /> {insertLabel}
                </Button>
            )}
        </Card>
    );
}

function ProofreadResult({ output, busy, editorRef }) {
    const terms = useMemo(() => extractProofreadTerms(output), [output]);
    useEffect(() => {
        if (!busy && terms.length) {
            const n = editorRef.current?.highlightProofreadIssues?.(terms);
            if (n > 0) toast.success(`${n} issue${n > 1 ? "s" : ""} highlighted inline`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busy, output]);
    if (!output && !busy) return null;
    return (
        <Card className="p-3 bg-background/60 flex-1 flex flex-col overflow-hidden min-h-[120px]">
            <ScrollArea className="flex-1 -mx-1 px-1">
                <pre data-testid="proofread-output" className="whitespace-pre-wrap font-editor text-sm leading-relaxed text-foreground/90 pr-2">
                    {output}{busy && <span className="shimmer">▍</span>}
                </pre>
            </ScrollArea>
            {!busy && output && (
                <Button data-testid="clear-highlights" size="sm" variant="ghost" className="mt-1 text-xs"
                    onClick={() => editorRef.current?.clearProofreadIssues?.()}>
                    Clear inline highlights
                </Button>
            )}
        </Card>
    );
}

function ProofreadPanel({ novelId, chapterId, model, editorRef, onIssueCountChange }) {
    const gate = useGate();
    const nav = useNavigate();
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [issues, setIssues] = useState([]);
    const [appliedIds, setAppliedIds] = useState(new Set());
    const [ignoredIds, setIgnoredIds] = useState(new Set());
    const [showIgnored, setShowIgnored] = useState(false);

    useEffect(() => {
        if (!onIssueCountChange) return;
        const unresolved = issues.filter(i => !appliedIds.has(i.id) && !ignoredIds.has(i.id)).length;
        onIssueCountChange(unresolved);
    }, [issues, appliedIds, ignoredIds, onIssueCountChange]);

    async function runFor(text, scope) {
        if (!gate.canAI) { gate.openPaywall("ai"); return; }
        if (!text || !text.trim()) { toast.error(`No ${scope} text`); return; }
        setBusy(true);
        setIssues([]);
        setAppliedIds(new Set());
        setIgnoredIds(new Set());
        setShowIgnored(false);
        try {
            const res = await proofreadJSON(text, model, novelId, chapterId);
            const found = res.issues || [];
            setIssues(found);
            if (found.length) {
                editorRef.current?.highlightProofreadIssues?.(found.map(i => i.text));
                toast.success(`${found.length} issue${found.length > 1 ? "s" : ""} found`);
            } else {
                toast.success("No issues found");
            }
        } catch (e) {
            const r = e?.response;
            handleAIError({
                status: r?.status,
                reason: r?.headers?.["x-dd-reason"] || "",
                detail: typeof r?.data?.detail === "string" ? r.data.detail : "Proofread failed",
            }, gate, nav);
        }
        setBusy(false);
    }

    function applyIssue(issue) {
        const ok = editorRef.current?.applyProofreadFix?.(issue.text, issue.suggestion);
        if (ok) {
            setAppliedIds(prev => new Set([...prev, issue.id]));
            setIgnoredIds(prev => { const n = new Set(prev); n.delete(issue.id); return n; });
            toast.success("Fix applied (tracked change)");
        } else {
            toast.error("Couldn't locate text in editor");
        }
    }
    function ignoreIssue(issue) {
        setIgnoredIds(prev => new Set([...prev, issue.id]));
        toast.success("Suggestion ignored — highlight kept for later", { duration: 2500 });
    }
    function restoreIssue(issue) {
        setIgnoredIds(prev => { const n = new Set(prev); n.delete(issue.id); return n; });
    }
    function applyAll() {
        let count = 0;
        const applyable = issues.filter(i => !appliedIds.has(i.id) && !ignoredIds.has(i.id));
        for (const issue of applyable) {
            if (editorRef.current?.applyProofreadFix?.(issue.text, issue.suggestion)) count += 1;
        }
        setAppliedIds(prev => {
            const n = new Set(prev);
            applyable.forEach(i => n.add(i.id));
            return n;
        });
        toast.success(`Applied ${count} fix${count !== 1 ? "es" : ""}`);
    }

    const ignoredCount = ignoredIds.size;
    const visibleIssues = showIgnored ? issues : issues.filter(i => !ignoredIds.has(i.id));
    const unresolvedCount = issues.filter(i => !appliedIds.has(i.id) && !ignoredIds.has(i.id)).length;

    return (
        <>
            <p className="text-xs text-muted-foreground">{t("ws.proof.desc")}</p>
            <div className="flex gap-2">
                <LockedButton locked={!gate.canAI} reason="ai"
                    data-testid="proofread-selection" disabled={busy}
                    onClick={() => runFor(editorRef.current?.getSelectedText?.() || "", "selection")}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("ws.proof.selection")}
                </LockedButton>
                <LockedButton locked={!gate.canAI} reason="ai"
                    data-testid="proofread-chapter" variant="outline" disabled={busy}
                    onClick={() => runFor(editorRef.current?.getPlainText?.() || "", "chapter")}>
                    {t("ws.proof.wholeChapter")}
                </LockedButton>
            </div>

            {issues.length > 0 && (
                <div className="flex items-center justify-between -mb-1 gap-2">
                    <span className="ui-label truncate" data-testid="proofread-counts">
                        {issues.length} {issues.length === 1 ? t("ws.proof.issueOne") : t("ws.proof.issueMany")}
                        {ignoredCount > 0 && <span className="text-muted-foreground/70"> · {ignoredCount} {t("ws.proof.ignored")}</span>}
                    </span>
                    <div className="flex gap-1">
                        {ignoredCount > 0 && (
                            <Button data-testid="toggle-show-ignored" size="sm" variant="ghost" className="h-7 text-xs"
                                onClick={() => setShowIgnored(v => !v)}
                                title={showIgnored ? t("ws.proof.hideIgnored") : t("ws.proof.showIgnored")}>
                                {showIgnored
                                    ? <><EyeOff className="w-3 h-3" /> {t("ws.proof.hideIgnored")}</>
                                    : <><Eye className="w-3 h-3" /> {t("ws.proof.showIgnored")}</>}
                            </Button>
                        )}
                        <Button data-testid="apply-all-fixes" size="sm" variant="default" className="h-7 text-xs"
                            disabled={unresolvedCount === 0}
                            onClick={applyAll}>
                            <Check className="w-3 h-3" /> {t("ws.proof.applyAll")}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={() => { setIssues([]); setAppliedIds(new Set()); setIgnoredIds(new Set()); setShowIgnored(false); editorRef.current?.clearProofreadIssues?.(); }}>
                            <X className="w-3 h-3" /> {t("ws.proof.dismiss")}
                        </Button>
                    </div>
                </div>
            )}
            <ScrollArea className="flex-1 -mx-1">
                <div className="space-y-2 pr-1 pl-1 pb-2">
                    {!busy && issues.length === 0 && (
                        <p className="text-xs text-muted-foreground italic mt-2">{t("ws.proof.runToSee")}</p>
                    )}
                    {!busy && issues.length > 0 && visibleIssues.length === 0 && (
                        <p data-testid="all-ignored-empty-state" className="text-xs text-muted-foreground italic mt-2">
                            {t("ws.proof.allIgnoredPre")}<span className="font-medium">{t("ws.proof.allIgnoredStrong")}</span>{t("ws.proof.allIgnoredPost")}
                        </p>
                    )}
                    {busy && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="w-3 h-3 animate-spin" /> {t("ws.proof.analyzing")}
                        </div>
                    )}
                    {visibleIssues.map((iss) => {
                        const applied = appliedIds.has(iss.id);
                        const ignored = ignoredIds.has(iss.id);
                        return (
                            <Card key={iss.id} data-testid={`issue-card-${iss.id}`}
                                data-ignored={ignored ? "true" : "false"}
                                className={`p-3 text-xs bg-background/60 ${applied ? "opacity-50" : ""} ${ignored ? "border-dashed opacity-70" : ""}`}>
                                <div className="font-editor leading-relaxed">
                                    <span className="line-through text-destructive/70">{iss.text}</span>
                                    <span className="text-muted-foreground"> → </span>
                                    <span className="text-primary font-medium">{iss.suggestion || t("ws.proof.remove")}</span>
                                </div>
                                {iss.reason && <p className="mt-1.5 text-[11px] text-muted-foreground italic">{iss.reason}</p>}
                                {!applied && !ignored && (
                                    <div className="mt-2 flex gap-1">
                                        <Button data-testid={`apply-fix-${iss.id}`} size="sm" variant="secondary" className="h-7 text-xs"
                                            onClick={() => applyIssue(iss)}>
                                            <Check className="w-3 h-3" /> {t("ws.proof.apply")}
                                        </Button>
                                        <Button data-testid={`ignore-fix-${iss.id}`} size="sm" variant="ghost" className="h-7 text-xs"
                                            onClick={() => ignoreIssue(iss)}
                                            title="Hide this suggestion but keep the inline highlight">
                                            <EyeOff className="w-3 h-3" /> {t("ws.proof.ignore")}
                                        </Button>
                                    </div>
                                )}
                                {ignored && !applied && (
                                    <div className="mt-2 flex items-center justify-between gap-2">
                                        <p className="text-[11px] text-muted-foreground italic">
                                            <EyeOff className="w-3 h-3 inline mr-1" /> {t("ws.proof.ignoredKept")}
                                        </p>
                                        <Button data-testid={`restore-fix-${iss.id}`} size="sm" variant="ghost" className="h-7 text-xs"
                                            onClick={() => restoreIssue(iss)}>
                                            <Eye className="w-3 h-3" /> {t("ws.proof.restore")}
                                        </Button>
                                    </div>
                                )}
                                {applied && <p className="mt-1.5 text-[11px] text-primary"><Check className="w-3 h-3 inline" /> {t("ws.proof.appliedTracked")}</p>}
                            </Card>
                        );
                    })}
                </div>
            </ScrollArea>
        </>
    );
}

function EditableTitle({ value, onSave }) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    useEffect(() => { setDraft(value); }, [value]);

    function commit() {
        setEditing(false);
        const t = draft.trim();
        if (t && t !== value) onSave(t);
        else setDraft(value);
    }
    function onKeyDown(e) {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); setEditing(false); setDraft(value); }
    }
    if (editing) {
        return (
            <input
                data-testid="novel-title-edit-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={onKeyDown}
                autoFocus
                className="font-heading text-xl bg-transparent border-b border-primary/60 outline-none px-1 min-w-[160px] max-w-[420px]"
            />
        );
    }
    return (
        <button
            data-testid="novel-title-display"
            onClick={() => setEditing(true)}
            title={t("ws.rename")}
            className="font-heading text-xl truncate text-left hover:text-primary transition-colors max-w-[420px]"
        >
            {value}
        </button>
    );
}

function SortableChapterItem({ chapter: c, isActive, onSelect, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };
    return (
        <li ref={setNodeRef} style={style}>
            <button
                data-testid={`chapter-item-${c.id}`}
                onClick={onSelect}
                className={`group w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 flex items-center gap-2 ${isActive ? "bg-secondary text-foreground" : "hover:bg-muted/60 hover:text-foreground text-muted-foreground"}`}>
                <span
                    {...attributes}
                    {...listeners}
                    data-testid={`chapter-drag-${c.id}`}
                    className="cursor-grab active:cursor-grabbing flex items-center -ml-1 px-1 py-1 text-muted-foreground/60 hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ChevronRight className={`w-3 h-3 ${isActive ? "text-primary" : ""}`} />
                </span>
                <span className="font-heading text-base flex-1 truncate">{c.title}</span>
                <span className="text-[10px] opacity-50">{htmlToWordCount(c.content)}</span>
                <Trash2 data-testid={`delete-chapter-${c.id}`}
                    onClick={onDelete}
                    className="w-3 h-3 opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:text-destructive" />
            </button>
        </li>
    );
}

function ShareButton({ novel, onUpdated }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const enabled = !!(novel.share_enabled && novel.share_token);
    const shareUrl = enabled ? `${window.location.origin}/read/${novel.share_token}` : "";

    async function enable() {
        setBusy(true);
        try {
            const res = await enableShare(novel.id);
            onUpdated({ ...novel, share_enabled: true, share_token: res.share_token });
            toast.success(t("ws.share.enabled"));
        } catch { toast.error("Couldn't enable share"); }
        setBusy(false);
    }
    async function revoke() {
        if (!confirm("Revoke this share link? Anyone with it will lose access.")) return;
        setBusy(true);
        try {
            await disableShare(novel.id);
            onUpdated({ ...novel, share_enabled: false });
            toast.success(t("ws.share.revoked"));
        } catch { toast.error("Couldn't revoke"); }
        setBusy(false);
    }
    async function copy() {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success(t("ws.share.copied"));
        } catch { toast.error("Couldn't copy"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="share-button" variant="outline" size="sm">
                    <Share2 className="w-4 h-4" /> {t("ws.share.button")}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal">{t("ws.share.title")}</DialogTitle>
                    <DialogDescription>{t("ws.share.desc")}</DialogDescription>
                </DialogHeader>
                {enabled ? (
                    <div className="space-y-3">
                        <div className="flex gap-2">
                            <Input data-testid="share-link-input" value={shareUrl} readOnly className="font-mono text-xs" />
                            <Button data-testid="copy-share-link" variant="default" onClick={copy}><Copy className="w-4 h-4" /> {t("ws.share.copy")}</Button>
                        </div>
                        <a href={shareUrl} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                            <LinkIcon className="w-3 h-3" /> {t("ws.share.openPreview")}
                        </a>
                        <div className="border-t border-border/60 pt-3">
                            <Button data-testid="revoke-share" variant="ghost" size="sm" disabled={busy}
                                onClick={revoke} className="text-destructive hover:text-destructive">
                                <X className="w-3 h-3" /> {t("ws.share.revoke")}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{t("ws.share.generateDesc")}</p>
                        <Button data-testid="enable-share" disabled={busy} onClick={enable}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} {t("ws.share.generate")}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function CommentsButton({ novelId, chapters = [] }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const chapterTitles = useMemo(() => {
        const m = {};
        chapters.forEach((c, i) => { m[c.id] = c.title || `Chapter ${i + 1}`; });
        return m;
    }, [chapters]);

    async function load() {
        setLoading(true);
        try { setItems(await listNovelComments(novelId)); } catch (e) { console.error("Comments load failed", e); }
        setLoading(false);
    }
    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line
    }, [open]);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [novelId]);

    async function remove(id) {
        if (!confirm("Delete this note?")) return;
        await deleteComment(id);
        setItems(items.filter(i => i.id !== id));
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="comments-button" variant="outline" size="sm" className="relative">
                    <MessageCircle className="w-4 h-4" /> {t("ws.notes.button")}
                    {items.length > 0 && (
                        <span data-testid="comments-badge"
                            className="absolute -top-1 -right-1 text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center leading-none">
                            {items.length}
                        </span>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal">{t("ws.notes.title")}</DialogTitle>
                    <DialogDescription>{t("ws.notes.desc")}</DialogDescription>
                </DialogHeader>
                <ScrollArea className="max-h-[60vh]">
                    <div className="space-y-2 pr-2">
                        {loading && <p className="text-xs text-muted-foreground">{t("common.loading")}</p>}
                        {!loading && items.length === 0 && <p className="text-xs text-muted-foreground italic">{t("ws.notes.empty")}</p>}
                        {items.map(c => (
                            <Card key={c.id} data-testid={`note-${c.id}`} className="p-3 bg-background/60">
                                <div className="flex items-center justify-between">
                                    <span className="font-heading text-base">{c.name}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="ui-label">{new Date(c.created_at).toLocaleString()}</span>
                                        <Trash2 data-testid={`delete-note-${c.id}`} className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive cursor-pointer"
                                            onClick={() => remove(c.id)} />
                                    </div>
                                </div>
                                {c.chapter_id && chapterTitles[c.chapter_id] && (
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-primary/80" data-testid={`note-chapter-${c.id}`}>
                                        {chapterTitles[c.chapter_id]}
                                    </div>
                                )}
                                {c.anchor && (
                                    <div className="mt-1.5 flex items-start gap-1.5 text-xs italic text-foreground/70 border-l-2 border-primary/50 pl-2" data-testid={`note-anchor-${c.id}`}>
                                        <Quote className="w-3 h-3 mt-0.5 shrink-0 text-primary" />
                                        <span className="line-clamp-2">&ldquo;{c.anchor}&rdquo;</span>
                                    </div>
                                )}
                                <p className="text-sm font-editor leading-relaxed mt-1.5 text-foreground/90 whitespace-pre-wrap">{c.text}</p>
                            </Card>
                        ))}
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}


function CoverButton({ novel, onUpdated }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [busyAction, setBusyAction] = useState(""); // "upload" | "delete"
    const [open, setOpen] = useState(false);
    const fileRef = useRef(null);

    async function onPickFile(e) {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!/^image\/(jpeg|jpg|png|webp)$/.test(f.type)) {
            toast.error("Use a JPG, PNG, or WEBP image"); return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Image too large (max 8 MB)"); return;
        }
        setBusy(true); setBusyAction("upload");
        try {
            const res = await uploadCover(novel.id, f);
            onUpdated({ ...novel, cover_url: res.cover_url });
            toast.success("Cover uploaded");
            setOpen(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        }
        setBusy(false); setBusyAction("");
    }

    async function onDelete() {
        if (!confirm("Remove this cover?")) return;
        setBusy(true); setBusyAction("delete");
        try {
            await deleteCover(novel.id);
            onUpdated({ ...novel, cover_url: "" });
            toast.success("Cover removed");
        } catch {
            toast.error("Delete failed");
        }
        setBusy(false); setBusyAction("");
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <div className="flex items-center gap-2">
                {novel.cover_url && (
                    <button data-testid="cover-thumb" onClick={() => setOpen(true)}
                        className="w-7 h-9 rounded-sm overflow-hidden border border-border shrink-0">
                        <img src={absUrl(novel.cover_url)} alt="cover" className="w-full h-full object-cover" />
                    </button>
                )}
                <Button data-testid="cover-button" variant="outline" size="sm" onClick={() => setOpen(true)}>
                    <ImageIcon className="w-4 h-4" />
                    {novel.cover_url ? t("ws.cover.cover") : t("ws.cover.addCover")}
                </Button>
            </div>
            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="cover-file-input"
                onChange={onPickFile}
            />
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal">{t("ws.cover.title")}</DialogTitle>
                    <DialogDescription>{t("ws.cover.desc")}</DialogDescription>
                </DialogHeader>
                {novel.cover_url && (
                    <img src={absUrl(novel.cover_url)} alt="Current cover"
                        className="rounded-md w-full max-h-[420px] object-contain bg-black/5" data-testid="cover-preview-img" />
                )}
                <div className="mt-2">
                    <Button data-testid="upload-cover-button" variant="outline" disabled={busy}
                        className="w-full" onClick={() => fileRef.current?.click()}>
                        {busyAction === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {t("ws.cover.upload")}
                    </Button>
                </div>
                {novel.cover_url && (
                    <Button data-testid="delete-cover-button" variant="ghost" disabled={busy}
                        onClick={onDelete}
                        className="text-destructive hover:text-destructive">
                        {busyAction === "delete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {t("ws.cover.remove")}
                    </Button>
                )}
                <p className="text-[11px] text-muted-foreground">{t("ws.cover.formats")}</p>
            </DialogContent>
        </Dialog>
    );
}

function ChapterMetaBar({ wc, chapter, onGoalChange }) {
    const { t } = useTranslation();
    const goal = chapter?.word_goal || 0;
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(goal);
    useEffect(() => { setVal(goal); }, [goal]);
    const pct = goal > 0 ? Math.min(100, Math.round((wc / goal) * 100)) : 0;
    return (
        <div className="flex items-center justify-between gap-3 mb-10">
            <div className="ui-label">{wc} {t("ws.words")} · {Math.max(1, Math.ceil(wc / 250))} {t("ws.meta.minRead")}</div>
            <div className="flex items-center gap-2">
                {goal > 0 && (
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="chapter-goal-progress">
                        <Target className="w-3 h-3 text-primary" />
                        <div className="w-24">
                            <Progress value={pct} className="h-1" />
                        </div>
                        <span className="tabular-nums">{wc}/{goal}</span>
                    </div>
                )}
                {editing ? (
                    <form onSubmit={(e) => { e.preventDefault(); onGoalChange(Number(val) || 0); setEditing(false); }}
                        className="flex items-center gap-1">
                        <Input type="number" data-testid="chapter-goal-input" value={val}
                            onChange={(e) => setVal(e.target.value)} className="h-7 w-20 text-xs" min={0} step={100} autoFocus />
                        <Button size="sm" type="submit" className="h-7 text-xs">{t("ws.meta.set")}</Button>
                    </form>
                ) : (
                    <Button data-testid="set-chapter-goal" variant="ghost" size="sm" className="h-7 text-[11px]"
                        onClick={() => setEditing(true)}>
                        {goal > 0 ? t("ws.meta.editGoal") : t("ws.meta.addGoal")}
                    </Button>
                )}
            </div>
        </div>
    );
}

const SCENE_STATUS = {
    todo: { icon: Circle, color: "text-muted-foreground", label: "ws.scene.todo" },
    draft: { icon: CircleDot, color: "text-amber-600", label: "ws.scene.draft" },
    done: { icon: CircleCheck, color: "text-primary", label: "ws.scene.done" },
};

function SceneStrip({ chapterId }) {
    const { t } = useTranslation();
    const [scenes, setScenes] = useState([]);
    const [adding, setAdding] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingForm, setEditingForm] = useState({ title: "", summary: "", status: "todo" });
    const sceneSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    async function load() {
        if (!chapterId) { setScenes([]); return; }
        try { setScenes(await listScenes(chapterId)); } catch { setScenes([]); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [chapterId]);

    async function add(e) {
        e?.preventDefault?.();
        if (!newTitle.trim()) return;
        const s = await createScene(chapterId, { title: newTitle, status: "todo" });
        setScenes([...scenes, s]);
        setNewTitle(""); setAdding(false);
    }
    async function cycleStatus(s) {
        const order = ["todo", "draft", "done"];
        const next = order[(order.indexOf(s.status) + 1) % order.length];
        const u = await updateScene(s.id, { status: next });
        setScenes(scenes.map(x => x.id === s.id ? u : x));
    }
    async function saveEdit() {
        if (!editingId) return;
        const u = await updateScene(editingId, editingForm);
        setScenes(scenes.map(x => x.id === editingId ? u : x));
        setEditingId(null);
    }
    async function remove(id) {
        if (!confirm("Delete scene?")) return;
        await deleteScene(id);
        setScenes(scenes.filter(x => x.id !== id));
    }

    if (!chapterId) return null;

    return (
        <div className="mb-10" data-testid="scene-strip">
            <div className="flex items-center justify-between mb-3">
                <span className="ui-label">{t("ws.scenes")}</span>
                <div className="flex gap-1">
                    {!adding && (
                        <Button data-testid="add-scene-button" variant="ghost" size="sm" className="h-7 text-xs"
                            onClick={() => setAdding(true)}>
                            <Plus className="w-3 h-3" /> {t("ws.scene.add")}
                        </Button>
                    )}
                </div>
            </div>
            {adding && (
                <form onSubmit={add} className="flex gap-2 mb-3">
                    <Input data-testid="new-scene-input" autoFocus value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder={t("ws.scene.placeholder")} className="text-sm" />
                    <Button data-testid="save-scene" size="sm" type="submit">{t("common.add")}</Button>
                    <Button size="sm" variant="ghost" type="button" onClick={() => { setAdding(false); setNewTitle(""); }}>{t("common.cancel")}</Button>
                </form>
            )}
            <DndContext
                sensors={sceneSensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => {
                    const { active, over } = e;
                    if (!over || active.id === over.id) return;
                    const oldIdx = scenes.findIndex(s => s.id === active.id);
                    const newIdx = scenes.findIndex(s => s.id === over.id);
                    if (oldIdx < 0 || newIdx < 0) return;
                    const next = arrayMove(scenes, oldIdx, newIdx);
                    setScenes(next.map((s, i) => ({ ...s, order: i + 1 })));
                    reorderScenes(chapterId, next.map(s => s.id)).catch(() => toast.error("Reorder failed"));
                }}
            >
                <SortableContext items={scenes.map(s => s.id)} strategy={horizontalListSortingStrategy}>
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                        {scenes.length === 0 && !adding && (
                            <p className="text-[11px] text-muted-foreground italic">{t("ws.scene.empty")}</p>
                        )}
                        {scenes.map((s) => (
                            <SortableSceneCard
                                key={s.id}
                                scene={s}
                                onCycleStatus={() => cycleStatus(s)}
                                onEdit={() => { setEditingId(s.id); setEditingForm({ title: s.title, summary: s.summary, status: s.status }); }}
                                onRemove={() => remove(s.id)}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            <Dialog open={!!editingId} onOpenChange={(o) => { if (!o) setEditingId(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-normal">{t("ws.scene.edit")}</DialogTitle>
                        <DialogDescription>{t("ws.scene.updateDesc")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="ui-label">{t("ws.scene.title")}</Label>
                            <Input data-testid="edit-scene-title" value={editingForm.title}
                                onChange={(e) => setEditingForm({ ...editingForm, title: e.target.value })} className="mt-1.5" /></div>
                        <div><Label className="ui-label">{t("ws.scene.summary")}</Label>
                            <Textarea data-testid="edit-scene-summary" rows={3} value={editingForm.summary}
                                onChange={(e) => setEditingForm({ ...editingForm, summary: e.target.value })} className="mt-1.5" /></div>
                        <div><Label className="ui-label">{t("ws.scene.status")}</Label>
                            <Select value={editingForm.status} onValueChange={(v) => setEditingForm({ ...editingForm, status: v })}>
                                <SelectTrigger data-testid="edit-scene-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="todo">{t("ws.scene.todo")}</SelectItem>
                                    <SelectItem value="draft">{t("ws.scene.draft")}</SelectItem>
                                    <SelectItem value="done">{t("ws.scene.done")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button data-testid="save-scene-edit" onClick={saveEdit}>{t("common.save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SortableSceneCard({ scene: s, onCycleStatus, onEdit, onRemove }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const StatusIcon = SCENE_STATUS[s.status].icon;
    return (
        <Card ref={setNodeRef} style={style} data-testid={`scene-card-${s.id}`}
            className="shrink-0 w-52 p-3 bg-background/70 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-1">
                    <span {...attributes} {...listeners} data-testid={`scene-drag-${s.id}`}
                        className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground select-none px-0.5">⋮⋮</span>
                    <button data-testid={`scene-status-${s.id}`} onClick={onCycleStatus} title={t(SCENE_STATUS[s.status].label)}>
                        <StatusIcon className={`w-4 h-4 ${SCENE_STATUS[s.status].color}`} />
                    </button>
                </div>
                <div className="flex gap-1">
                    <button data-testid={`edit-scene-${s.id}`} className="text-muted-foreground hover:text-foreground"
                        onClick={onEdit}>
                        <Feather className="w-3 h-3" />
                    </button>
                    <button data-testid={`delete-scene-${s.id}`} className="text-muted-foreground hover:text-destructive"
                        onClick={onRemove}>
                        <Trash2 className="w-3 h-3" />
                    </button>
                </div>
            </div>
            <div className="font-heading text-sm leading-tight mb-1 line-clamp-2">{s.title}</div>
            {s.summary && <p className="text-[11px] text-muted-foreground line-clamp-3">{s.summary}</p>}
        </Card>
    );
}

function ExportMenu({ novelId }) {
    const { t } = useTranslation();
    async function download(format) {
        const toastId = toast.loading(`Preparing ${format.toUpperCase()}…`);
        try {
            const res = await fetch(exportNovelUrl(novelId, format), { credentials: "include" });
            if (!res.ok) {
                // Surface the real reason — a silent failure here is impossible to debug.
                let detail = "";
                try { detail = (await res.json())?.detail || ""; } catch { /* not JSON */ }
                throw new Error(detail || `server returned ${res.status}`);
            }
            const blob = await res.blob();
            if (!blob.size) throw new Error("the file came back empty");
            const filename = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1]
                || `novel.${format}`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            a.remove(); URL.revokeObjectURL(url);
            toast.success(`Downloaded ${filename}`, { id: toastId });
        } catch (e) {
            toast.error(`Export failed — ${e.message || "unknown error"}`, { id: toastId, duration: 6000 });
        }
    }
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button data-testid="export-menu-trigger" variant="outline" size="sm">
                    <Download className="w-4 h-4" /> {t("ws.export.button")}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-docx" onClick={() => download("docx")}><FileText className="w-4 h-4" /> .docx</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-epub" onClick={() => download("epub")}><FileText className="w-4 h-4" /> .epub</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-pdf" onClick={() => download("pdf")}><FileText className="w-4 h-4" /> .pdf</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function VersionsDialog({ chapterId, onRestore }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [previewing, setPreviewing] = useState(null);
    useEffect(() => {
        if (open && chapterId) {
            listVersions(chapterId).then(setItems).catch(() => setItems([]));
            setPreviewing(null);
        }
    }, [open, chapterId]);
    async function preview(id) {
        const v = await getVersion(chapterId, id);
        setPreviewing(v);
    }
    async function restore(id) {
        if (!confirm("Restore this version? Current content will be saved as a new version.")) return;
        await restoreVersion(chapterId, id);
        toast.success("Restored");
        setOpen(false);
        onRestore?.();
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="versions-button" variant="outline" size="sm" disabled={!chapterId}>
                    <History className="w-4 h-4" /> {t("ws.versions.button")}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal">{t("ws.versions.title")}</DialogTitle>
                    <DialogDescription>{t("ws.versions.desc")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 max-h-[60vh]">
                    <div className="border-r pr-3 overflow-auto">
                        {items.length === 0 && <p className="text-xs text-muted-foreground italic">{t("ws.versions.none")}</p>}
                        <ul className="space-y-1">
                            {items.map(v => (
                                <li key={v.id}>
                                    <button data-testid={`version-item-${v.id}`}
                                        onClick={() => preview(v.id)}
                                        className={`w-full text-left p-2 rounded text-sm transition-colors hover:bg-muted ${previewing?.id === v.id ? "bg-secondary" : ""}`}>
                                        <div className="font-medium text-foreground">{new Date(v.created_at).toLocaleString()}</div>
                                        <div className="text-xs text-muted-foreground">{v.word_count} {t("ws.versions.wordsBy")} {v.title}</div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="overflow-auto">
                        {previewing ? (
                            <div>
                                <div
                                    data-testid="version-preview"
                                    className="prose-quill text-sm font-editor"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewing.content) || "<em>Empty</em>" }} />
                            </div>
                        ) : <p className="text-xs text-muted-foreground italic">{t("ws.versions.selectPreview")}</p>}
                    </div>
                </div>
                <DialogFooter>
                    {previewing && <Button data-testid="restore-version-button" onClick={() => restore(previewing.id)}>{t("ws.versions.restoreThis")}</Button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CharactersPanel({ novelId, onAI, aiBusy, aiOutput, onCountChange }) {
    const gate = useGate();
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", role: "", description: "", traits: "", arc: "", color: "" });

    async function load() { setItems(await listCharacters(novelId)); }
    useEffect(() => { load(); }, [novelId]);
    useEffect(() => { onCountChange?.(items.length); }, [items, onCountChange]);

    async function add(e) {
        e.preventDefault();
        if (!form.name.trim()) return;
        const created = await createCharacter(novelId, form);
        setItems([created, ...items]);
        setForm({ name: "", role: "", description: "", traits: "", arc: "", color: "" });
        setOpen(false);
    }
    async function remove(id) {
        if (!confirm("Delete character?")) return;
        await deleteCharacter(id); load();
    }
    async function cycleColor(c) {
        const idx = CHARACTER_PALETTE.indexOf(c.color || "");
        const next = CHARACTER_PALETTE[(idx + 1) % CHARACTER_PALETTE.length];
        const updated = await updateCharacter(c.id, { color: next });
        setItems(items.map(x => x.id === c.id ? updated : x));
    }

    // Pre-pick a sensible default colour for the create form so user can see what they'll get
    function pickDefaultColor() {
        const used = new Set(items.map(i => i.color));
        const free = CHARACTER_PALETTE.find(c => !used.has(c));
        return free || CHARACTER_PALETTE[items.length % CHARACTER_PALETTE.length];
    }

    return (
        <div className="h-full flex flex-col p-4 gap-3">
            <div className="flex items-center justify-between">
                <span className="ui-label">{t("ws.cast.bible")}</span>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && !form.color) setForm(f => ({ ...f, color: pickDefaultColor() })); }}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-character-button" size="sm"><Plus className="w-3 h-3" /> {t("common.add")}</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl font-normal">{t("ws.cast.newCharacter")}</DialogTitle>
                            <DialogDescription>{t("ws.cast.addToBible")}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={add} className="space-y-3">
                            <div><Label className="ui-label">{t("ws.cast.name")}</Label><Input data-testid="char-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                            <div>
                                <Label className="ui-label">{t("ws.cast.color")}</Label>
                                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="char-color-picker">
                                    {CHARACTER_PALETTE.map(c => (
                                        <button type="button" key={c}
                                            data-testid={`char-color-${c.replace('#', '')}`}
                                            onClick={() => setForm({ ...form, color: c })}
                                            className={`w-7 h-7 rounded-full transition-transform border ${form.color === c ? "ring-2 ring-offset-2 ring-foreground/60 scale-110" : "border-border/60 hover:scale-105"}`}
                                            style={{ background: c }}
                                            aria-label={c}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div><Label className="ui-label">{t("ws.cast.role")}</Label><Input data-testid="char-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder={t("ws.cast.rolePh")} className="mt-1.5" /></div>
                            <div><Label className="ui-label">{t("ws.cast.description")}</Label><Textarea data-testid="char-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
                            <div><Label className="ui-label">{t("ws.cast.traits")}</Label><Input data-testid="char-traits" value={form.traits} onChange={(e) => setForm({ ...form, traits: e.target.value })} placeholder={t("ws.cast.traitsPh")} className="mt-1.5" /></div>
                            <div><Label className="ui-label">{t("ws.cast.arc")}</Label><Textarea data-testid="char-arc" rows={2} value={form.arc} onChange={(e) => setForm({ ...form, arc: e.target.value })} className="mt-1.5" /></div>
                            <DialogFooter><Button data-testid="save-char" type="submit">{t("common.save")}</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <ScrollArea className="flex-1">
                <div className="space-y-2 pr-1">
                    {items.length === 0 && <p className="text-xs text-muted-foreground italic">{t("ws.cast.noChars")}</p>}
                    {items.map(c => (
                        <Card key={c.id} data-testid={`character-${c.id}`}
                            className="relative p-3 pl-4 bg-background/60 overflow-hidden">
                            <button
                                data-testid={`char-color-stripe-${c.id}`}
                                onClick={() => cycleColor(c)}
                                title="Click to change color"
                                className="absolute left-0 top-0 bottom-0 w-1.5 hover:w-2 transition-all cursor-pointer"
                                style={{ background: c.color || "transparent" }}
                                aria-label={`Change color (current ${c.color || 'none'})`}
                            />
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color || "transparent", border: c.color ? "none" : "1px dashed currentColor" }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-heading text-lg leading-tight truncate">{c.name}</div>
                                        {c.role && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.role}</div>}
                                    </div>
                                </div>
                                <Trash2 data-testid={`delete-char-${c.id}`} className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive cursor-pointer" onClick={() => remove(c.id)} />
                            </div>
                            {c.description && <p className="text-xs mt-2 text-foreground/80">{c.description}</p>}
                            {c.traits && <p className="text-[11px] mt-1 text-muted-foreground italic">{c.traits}</p>}
                        </Card>
                    ))}
                </div>
            </ScrollArea>
            <div className="border-t border-border/60 pt-3">
                <LockedButton locked={!gate.canAI} reason="ai"
                    data-testid="generate-character-button" variant="outline" size="sm" className="w-full" disabled={aiBusy}
                    onClick={() => onAI("character", "Invent a vivid supporting character that fits this novel — give name, role, description, traits, and arc.")}>
                    <Wand2 className="w-3 h-3" /> {t("ws.cast.generateIdea")}
                </LockedButton>
                {(aiOutput || aiBusy) && (
                    <Card className="p-2 mt-2 bg-background/60 max-h-40 overflow-auto">
                        <pre className="whitespace-pre-wrap font-editor text-xs leading-relaxed">{aiOutput}{aiBusy && <span className="shimmer">▍</span>}</pre>
                    </Card>
                )}
            </div>
        </div>
    );
}

function PlotPanel({ novelId, onAI, aiBusy, aiOutput, insertAtCursor, onCountChange }) {
    const gate = useGate();
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", act: "", description: "" });

    async function load() { setItems(await listPlot(novelId)); }
    useEffect(() => { load(); }, [novelId]);
    useEffect(() => { onCountChange?.(items.length); }, [items, onCountChange]);

    async function add(e) {
        e.preventDefault();
        if (!form.title.trim()) return;
        await createPlot(novelId, form);
        setForm({ title: "", act: "", description: "" });
        setOpen(false); load();
    }
    async function remove(id) {
        if (!confirm("Delete plot point?")) return;
        await deletePlot(id); load();
    }

    return (
        <div className="h-full flex flex-col p-4 gap-3">
            <div className="flex items-center justify-between">
                <span className="ui-label">{t("ws.plot.beats")}</span>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-plot-button" size="sm"><Plus className="w-3 h-3" /> {t("common.add")}</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl font-normal">{t("ws.plot.newBeat")}</DialogTitle>
                            <DialogDescription>{t("ws.plot.addBeat")}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={add} className="space-y-3">
                            <div><Label className="ui-label">{t("ws.plot.title")}</Label><Input data-testid="plot-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
                            <div><Label className="ui-label">{t("ws.plot.act")}</Label><Input data-testid="plot-act" value={form.act} onChange={(e) => setForm({ ...form, act: e.target.value })} placeholder={t("ws.plot.actPh")} className="mt-1.5" /></div>
                            <div><Label className="ui-label">{t("ws.plot.description")}</Label><Textarea data-testid="plot-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
                            <DialogFooter><Button data-testid="save-plot" type="submit">{t("common.save")}</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <ScrollArea className="flex-1">
                <div className="space-y-2 pr-1">
                    {items.length === 0 && <p className="text-xs text-muted-foreground italic">{t("ws.plot.noBeats")}</p>}
                    {items.map(p => (
                        <Card key={p.id} data-testid={`plot-${p.id}`} className="p-3 bg-background/60">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                    {p.act && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.act}</div>}
                                    <div className="font-heading text-base leading-tight">{p.title}</div>
                                </div>
                                <Trash2 data-testid={`delete-plot-${p.id}`} className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive cursor-pointer" onClick={() => remove(p.id)} />
                            </div>
                            {p.description && <p className="text-xs mt-2 text-foreground/80">{p.description}</p>}
                        </Card>
                    ))}
                </div>
            </ScrollArea>
            <div className="border-t border-border/60 pt-3 space-y-2">
                <LockedButton locked={!gate.canAI} reason="ai"
                    data-testid="generate-outline-button" variant="outline" size="sm" className="w-full" disabled={aiBusy}
                    onClick={() => onAI("outline", "Draft a 10-chapter outline that follows this novel's premise and characters. Use a three-act structure.")}>
                    <Wand2 className="w-3 h-3" /> {t("ws.plot.generateOutline")}
                </LockedButton>
                {(aiOutput || aiBusy) && (
                    <Card className="p-2 mt-1 bg-background/60 max-h-48 overflow-auto">
                        <pre className="whitespace-pre-wrap font-editor text-xs leading-relaxed">{aiOutput}{aiBusy && <span className="shimmer">▍</span>}</pre>
                        {aiOutput && !aiBusy && (
                            <Button size="sm" variant="ghost" className="mt-1" data-testid="insert-outline-button"
                                onClick={() => insertAtCursor("OUTLINE\n\n" + aiOutput)}>
                                {t("ws.plot.insertChapter")}
                            </Button>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}
